import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { ReadOnlyChip } from "../components/SlidePanel";
import { Btn, Empty } from "../components/UI";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { GENDERS, STATES, COUNTIES, MARITAL, EMPLOYMENT } from "./engagement/constants";
import { Camera, Paperclip, Download, Trash2, FileText, Image as ImageIcon, File } from "lucide-react";
import { toast } from "sonner";

const SALUTATIONS = ["Mr.", "Mrs.", "Ms.", "Mx.", "Dr.", "Rev."];
const ETHNICITIES = ["Hispanic or Latino", "Not Hispanic or Latino", "White", "Black or African American", "Asian", "American Indian or Alaska Native", "Native Hawaiian or Pacific Islander", "Multiracial", "Other", "Prefer not to say"];
const LANGUAGES = ["English", "Spanish", "French", "Portuguese", "Mandarin", "Vietnamese", "Arabic", "Russian", "ASL", "Other"];
const RELIGIONS = ["Christianity", "Islam", "Judaism", "Buddhism", "Hinduism", "Atheist/Agnostic", "Spiritual", "Other", "Prefer not to say"];
const EDUCATION = ["Less than High School", "High School / GED", "Some College", "Associate Degree", "Bachelor's Degree", "Master's Degree", "Doctorate", "Other"];
const SMOKING = ["Never Smoker", "Former Smoker", "Current Every Day Smoker", "Current Some Day Smoker", "Unknown"];
const MILITARY = ["None", "Active Duty", "Veteran", "Reserves", "Retired"];
const CHARACTERISTICS = [
  ["acquired_brain_injury", "Acquired Brain Injury"], ["autism_spectrum_disorder", "Autism Spectrum Disorder"],
  ["dementia", "Dementia"], ["developmental_disabilities", "Developmental Disabilities"],
  ["dual_mh_dd", "Dual Diagnosis - MH/DD"], ["dual_sud_dd", "Dual Diagnosis - SUD/DD"],
  ["dual_sud_mh", "Dual Diagnosis - SUD/MH"], ["hearing_impairments", "Hearing Impairments"],
  ["hiv_aids", "HIV positive/AIDS"], ["homeless", "Homeless Individual"],
  ["mental_disorders", "Mental Disorders"], ["new_immigrants", "New Immigrant"],
  ["other_addictions", "Other Addictions"], ["other_characteristic", "Other Characteristic"],
  ["physical_disabilities", "Physical Disabilities"], ["substance_use_disorders", "Substance Use Disorder/Addiction"],
  ["suicide_risk", "Suicide Risk"], ["unemployed_underemployed", "Unemployed/Underemployed"],
  ["unknown_characteristics", "Unknown"], ["visual_impairments", "Visual Impairments"], ["wheelchair", "Wheelchair"],
];
const PROFILE_KEYS = ["salutation", "nickname", "drivers_license", "weight", "height", "smoking_status", "preferred_language", "marital_status", "religion", "education_level", "employment", "military", "ethnicity"];

const Opt = (list) => list.map((o) => <option key={o} value={o}>{o}</option>);
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fileIcon = (ct) => (ct?.startsWith("image/") ? ImageIcon : ct === "application/pdf" ? FileText : File);

function resizeImage(file, max = 512) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export function ClientProfile({ client, onUpdated }) {
  const { user } = useAuth();
  const canDemo = canEdit(user, "demographics");
  const canDocs = canEdit(user, "documents");
  const photoInput = useRef(null);
  const attachInput = useRef(null);
  const [form, setForm] = useState({});
  const [chars, setChars] = useState([]);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const p = client.profile || {};
    setForm({
      first_name: client.first_name || "", last_name: client.last_name || "", dob: client.dob || "",
      gender: client.gender || "", phone: client.phone || "", email: client.email || "",
      address: client.address || "", city: client.city || "", state: client.state || "", zip: client.zip || "",
      status: client.status || "active", county: p.county || "",
      ...Object.fromEntries(PROFILE_KEYS.map((k) => [k, p[k] || ""])),
    });
    setChars(client.characteristics || []);
    api.get(`/clients/${client.id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const sel = (k, list, testId) => (
    <FSelect data-testid={testId} value={form[k] || ""} onChange={f(k)}>{Opt(list)}</FSelect>
  );

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error("First and last name are required"); return; }
    setBusy(true);
    try {
      const profile = { ...(client.profile || {}), county: form.county, ...Object.fromEntries(PROFILE_KEYS.map((k) => [k, form[k]])) };
      const body = {
        first_name: form.first_name, last_name: form.last_name, dob: form.dob, gender: form.gender,
        phone: form.phone, email: form.email, address: form.address, city: form.city, state: form.state,
        zip: form.zip, status: form.status, profile, characteristics: chars,
      };
      const r = await api.put(`/clients/${client.id}`, body);
      onUpdated?.(r.data);
      toast.success("Client profile saved");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    try {
      const dataUrl = await resizeImage(file);
      const r = await api.put(`/clients/${client.id}/photo`, { photo: dataUrl });
      onUpdated?.(r.data);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  const uploadFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1048576) { toast.error(`${file.name}: exceeds 10MB limit`); continue; }
      try {
        const dataUrl = await readAsDataURL(file);
        await api.post(`/clients/${client.id}/attachments`, { filename: file.name, content_type: file.type || "application/octet-stream", data: dataUrl });
        toast.success(`${file.name} uploaded`);
      } catch (err) { toast.error(`${file.name}: ${apiError(err)}`); }
    }
    setUploading(false);
    api.get(`/clients/${client.id}/attachments`).then((r) => setAttachments(r.data));
  };

  const download = async (a) => {
    try {
      const r = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (a) => {
    await api.delete(`/attachments/${a.id}`);
    setAttachments((list) => list.filter((x) => x.id !== a.id));
    toast.success("Attachment deleted");
  };

  return (
    <div data-testid="client-profile" className="space-y-6">
      <div className="flex flex-wrap gap-6 items-start">
        <div className="flex flex-col items-center gap-2">
          <button type="button" data-testid="upload-photo-btn" onClick={() => canDocs && photoInput.current?.click()} disabled={!canDocs}
            className="relative group h-28 w-28 rounded-2xl overflow-hidden border-2 border-dashed border-[#D7CCC8] hover:border-[#8D6E63] transition-colors bg-[#F5F2EB] flex items-center justify-center">
            {client.photo
              ? <img src={client.photo} alt={`${client.first_name} ${client.last_name}`} className="h-full w-full object-cover" />
              : <span className="font-heading text-2xl font-bold text-[#7B6157]">{client.first_name?.[0]}{client.last_name?.[0]}</span>}
            <span className="absolute inset-0 flex items-center justify-center bg-[#3E2723]/50 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={22} className="text-white" />
            </span>
          </button>
          <span className="text-xs text-[#84675B]">Click to {client.photo ? "change" : "upload"} photo</span>
          <input ref={photoInput} data-testid="profile-photo-input" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
        </div>

        <div className="flex-1 min-w-[280px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
          <div><FLabel>Salutation</FLabel>{sel("salutation", SALUTATIONS, "profile-salutation")}</div>
          <div><FLabel>First Name</FLabel><FInput data-testid="profile-first-name" value={form.first_name || ""} onChange={f("first_name")} /></div>
          <div><FLabel>Last Name</FLabel><FInput data-testid="profile-last-name" value={form.last_name || ""} onChange={f("last_name")} /></div>
          <div><FLabel>Nick Name</FLabel><FInput data-testid="profile-nickname" value={form.nickname || ""} onChange={f("nickname")} /></div>
          <div><FLabel>Date of Birth</FLabel><DateField testId="profile-dob" value={form.dob} onChange={(v) => setForm((s) => ({ ...s, dob: v }))} /></div>
          <div><FLabel>Gender</FLabel>{sel("gender", GENDERS, "profile-gender")}</div>
          <div><FLabel>Ethnicity</FLabel>{sel("ethnicity", ETHNICITIES, "profile-ethnicity")}</div>
          <div><FLabel>Driver's License Number</FLabel><FInput data-testid="profile-drivers-license" value={form.drivers_license || ""} onChange={f("drivers_license")} /></div>
          <div><FLabel>Enrollment Status</FLabel>
            <FSelect data-testid="profile-status" value={form.status || ""} onChange={f("status")}>
              <option value="active">Active</option><option value="referred">Referred</option><option value="discharged">Discharged</option>
            </FSelect>
          </div>
          <div><FLabel>Address</FLabel><FInput data-testid="profile-address" value={form.address || ""} onChange={f("address")} /></div>
          <div><FLabel>City</FLabel><FInput value={form.city || ""} onChange={f("city")} /></div>
          <div><FLabel>State</FLabel>{sel("state", STATES, "profile-state")}</div>
          <div><FLabel>Zip Code</FLabel><FInput value={form.zip || ""} onChange={f("zip")} /></div>
          <div><FLabel>County</FLabel>{sel("county", COUNTIES, "profile-county")}</div>
          <div><FLabel>Cell Phone Number</FLabel><FInput data-testid="profile-phone" value={form.phone || ""} onChange={f("phone")} /></div>
          <div><FLabel>Email</FLabel><FInput type="email" data-testid="profile-email" value={form.email || ""} onChange={f("email")} /></div>
          <div><FLabel>Weight (Pounds)</FLabel><FInput data-testid="profile-weight" value={form.weight || ""} onChange={f("weight")} /></div>
          <div><FLabel>Height (Ft ' in)</FLabel><FInput data-testid="profile-height" value={form.height || ""} onChange={f("height")} /></div>
          <div><FLabel>Smoking Status</FLabel>{sel("smoking_status", SMOKING, "profile-smoking")}</div>
          <div><FLabel>Preferred Language</FLabel>{sel("preferred_language", LANGUAGES, "profile-language")}</div>
          <div><FLabel>Marital Status</FLabel>{sel("marital_status", MARITAL, "profile-marital")}</div>
          <div><FLabel>Religion</FLabel>{sel("religion", RELIGIONS, "profile-religion")}</div>
          <div><FLabel>Education Level</FLabel>{sel("education_level", EDUCATION, "profile-education")}</div>
          <div><FLabel>Employment</FLabel>{sel("employment", EMPLOYMENT, "profile-employment")}</div>
          <div><FLabel>Military</FLabel>{sel("military", MILITARY, "profile-military")}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
        <div className="font-heading text-sm font-bold text-[#3E2723] mb-1">Characteristics</div>
        <p className="text-xs text-[#84675B] mb-3">Used for the annual Persons Served census (CARF / grant reporting). Select all that apply.</p>
        <div className="flex flex-wrap gap-2" data-testid="profile-characteristics">
          {CHARACTERISTICS.map(([key, label]) => {
            const on = chars.includes(key);
            return (
              <button key={key} type="button" data-testid={`char-${key}`} disabled={!canDemo}
                onClick={() => setChars((cs) => (on ? cs.filter((c) => c !== key) : [...cs, key]))}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#E5DFD3] bg-white text-[#6D4C41] hover:border-[#8D6E63]"} ${!canDemo ? "opacity-60 cursor-not-allowed" : ""}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {!canDemo && <ReadOnlyChip />}
        {canDemo && <Btn data-testid="profile-save-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Profile"}</Btn>}
      </div>

      <div className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]">
            <Paperclip size={15} className="text-[#7B6157]" /> Attachments <span className="text-[#84675B] font-normal">({attachments.length})</span>
          </div>
          {canDocs && <Btn data-testid="attachment-upload-btn" variant="outline" className="rounded-lg text-xs px-3 py-1.5" onClick={() => attachInput.current?.click()} disabled={uploading}>
            <Paperclip size={13} /> {uploading ? "Uploading…" : "Upload Files"}
          </Btn>}
          <input ref={attachInput} data-testid="attachment-upload-input" type="file" multiple className="hidden" onChange={uploadFiles} />
        </div>
        {attachments.length === 0 ? <Empty text="No attachments - upload IDs, insurance cards, records or any client documents (max 10MB each)" /> : (
          <div className="space-y-1.5">
            {attachments.map((a) => {
              const Icon = fileIcon(a.content_type);
              return (
                <div key={a.id} data-testid={`attachment-row-${a.id}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-white border border-[#EFEBE0] px-4 py-2.5">
                  <Icon size={16} className="text-[#7B6157] shrink-0" />
                  <span className="text-sm font-medium text-[#3E2723] break-all min-w-0 flex-1">{a.filename}</span>
                  <span className="text-xs text-[#84675B] whitespace-nowrap">{fmtSize(a.size)} · {dayjs(a.created_at).format("MM/DD/YYYY")} · {a.uploaded_by}</span>
                  <div className="flex gap-1.5">
                    <button type="button" data-testid={`attachment-download-${a.id}`} aria-label="Download" onClick={() => download(a)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC]"><Download size={13} /></button>
                    {canDocs && <button type="button" data-testid={`attachment-delete-${a.id}`} aria-label="Delete" onClick={() => remove(a)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
